import React, { useState, useEffect, useRef } from 'react';
import { ThumbsUp, Share2, Bell, ChevronDown, Plus, X , Filter} from 'lucide-react';

const dummyPosts = [
  { 
    id: 1, 
    username: 'user1', 
    content: 'Enjoying the sunset!', 
    caption: 'Beautiful evening',
    dateTime: '2024-09-17T18:30:00',
    likes: 5, 
    type: 'image', 
    url: 'https://www.claudeusercontent.com/api/placeholder/300/300',
    tags: ['sunset', 'nature']
  },
  { 
    id: 2, 
    username: 'user2', 
    content: 'Check out my new dance moves!', 
    caption: 'Dancing in the rain',
    dateTime: '2024-09-17T15:45:00',
    likes: 10, 
    type: 'video', 
    url: 'https://www.claudeusercontent.com/api/placeholder/300/300',
    tags: ['dance', 'fun']
  },
  { 
    id: 3, 
    username: 'user3', 
    content: 'New podcast episode!', 
    caption: 'Tech talk episode 5',
    dateTime: '2024-09-17T10:00:00',
    likes: 3, 
    type: 'audio', 
    url: 'https://www.claudeusercontent.com/api/placeholder/300/50',
    tags: ['podcast', 'tech']
  },
];

// Function to generate more dummy posts
const generateMorePosts = (startId, count) => {
  return Array.from({ length: count }, (_, index) => ({
    id: startId + index,
    username: `user${startId + index}`,
    content: `Generated post ${startId + index}`,
    caption: `Caption for post ${startId + index}`,
    dateTime: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
    likes: Math.floor(Math.random() * 100),
    type: ['image', 'video', 'audio'][Math.floor(Math.random() * 3)],
    url: 'https://www.claudeusercontent.com/api/placeholder/300/300',
    tags: ['generated', `tag${startId + index}`]
  }));
};

const Timeline = () => {
  const [posts, setPosts] = useState(dummyPosts);
  const [displayedPosts, setDisplayedPosts] = useState(posts);
  const [notifications, setNotifications] = useState(0);
  const [sortBy, setSortBy] = useState('dateTime');
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ file: null, caption: '', tags: '' });
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  const observer = useRef();
  const lastPostElementRef = useRef();

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 1.0
    };

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMorePosts();
      }
    }, options);

    if (lastPostElementRef.current) {
      observer.current.observe(lastPostElementRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [displayedPosts]);

  useEffect(() => {
    // Update available tags
    const tags = new Set(posts.flatMap(post => post.tags));
    setAvailableTags(Array.from(tags));

    // Apply filters
    filterPosts();
  }, [posts, selectedTags]);

  const loadMorePosts = () => {
    setLoading(true);
    setTimeout(() => {
      const newPosts = generateMorePosts(posts.length + 1, 5);
      setPosts(prevPosts => [...prevPosts, ...newPosts]);
      setLoading(false);
    }, 1000);
  };

  const handleLike = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId ? { ...post, likes: post.likes + 1 } : post
    ));
    setNotifications(prev => prev + 1);
  };

  const handleShare = (postId) => {
    console.log(`Sharing post ${postId}`);
  };

  const handleSort = (criteria) => {
    setSortBy(criteria);
    setDisplayedPosts(prevPosts => [...prevPosts].sort((a, b) => {
      if (criteria === 'dateTime') {
        return new Date(b.dateTime) - new Date(a.dateTime);
      } else if (criteria === 'likes') {
        return b.likes - a.likes;
      }
    }));
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    const newPostData = {
      id: posts.length + 1,
      username: 'currentUser',
      content: newPost.caption,
      caption: newPost.caption,
      dateTime: new Date().toISOString(),
      likes: 0,
      type: newPost.file ? newPost.file.type.split('/')[0] : 'text',
      url: newPost.file ? URL.createObjectURL(newPost.file) : null,
      tags: newPost.tags.split(',').map(tag => tag.trim())
    };
    setPosts(prevPosts => [newPostData, ...prevPosts]);
    setIsCreatePostOpen(false);
    setNewPost({ file: null, caption: '', tags: '' });
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prevTags => 
      prevTags.includes(tag)
        ? prevTags.filter(t => t !== tag)
        : [...prevTags, tag]
    );
  };

  const filterPosts = () => {
    if (selectedTags.length === 0) {
      setDisplayedPosts(posts);
    } else {
      const filtered = posts.filter(post => 
        selectedTags.every(tag => post.tags.includes(tag))
      );
      setDisplayedPosts(filtered);
    }
  };

  return (
    <div className="min-vh-100 bg-white">
      <header className="bg-success text-white p-3">
        <div className="container">
          <div className="row align-items-center">
            <div className="col">
              <h1 className="h4 mb-0">iDo</h1>
            </div>
            <div className="col-auto">
              <button 
                className="btn btn-sm btn-light me-2"
                onClick={() => setIsCreatePostOpen(true)}
              >
                <Plus size={24} />
              </button>
              <button className="btn btn-sm btn-light me-2 position-relative">
                <Bell size={24} />
                {notifications > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {notifications}
                    <span className="visually-hidden">unread messages</span>
                  </span>
                )}
              </button>
              <input 
                type="text" 
                placeholder="Search posts..." 
                className="form-control form-control-sm d-inline-block w-auto"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="container py-4">
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <div className="dropdown">
            <button
              className="btn btn-secondary dropdown-toggle"
              type="button"
              id="filterDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <Filter size={20} className="me-1" />
              Filter by Tags
            </button>
            <ul className="dropdown-menu" aria-labelledby="filterDropdown">
              {availableTags.map(tag => (
                <li key={tag}>
                  <a 
                    className="dropdown-item" 
                    href="#"
                    onClick={() => handleTagSelect(tag)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={() => {}}
                      className="me-2"
                    />
                    {tag}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="dropdown">
            <button
              className="btn btn-secondary dropdown-toggle"
              type="button"
              id="sortDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Sort by: {sortBy === 'dateTime' ? 'Latest' : 'Most Liked'}
            </button>
            <ul className="dropdown-menu" aria-labelledby="sortDropdown">
              <li><a className="dropdown-item" href="#" onClick={() => handleSort('dateTime')}>Latest</a></li>
              <li><a className="dropdown-item" href="#" onClick={() => handleSort('likes')}>Most Liked</a></li>
            </ul>
          </div>
        </div>

        {selectedTags.length > 0 && (
          <div className="mb-3">
            <strong>Filtered by tags: </strong>
            {selectedTags.map(tag => (
              <span key={tag} className="badge bg-primary me-1">{tag}</span>
            ))}
          </div>
        )}

        {displayedPosts.map((post, index) => (
          <div 
            key={post.id} 
            ref={index === displayedPosts.length - 1 ? lastPostElementRef : null}
            className="card mb-4 mx-auto"
            style={{ maxWidth: '350px' }}
          >
            {/* Post content (same as before) */}
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">{post.username}</span>
                <small className="text-muted">{new Date(post.dateTime).toLocaleString()}</small>
              </div>
              {(post.type === 'image' || post.type === 'video') && (
                <div className="mb-3 bg-light p-2 shadow-sm" style={{ transform: 'rotate(-2deg)' }}>
                  <img src={post.url} alt="Post content" className="img-fluid mb-2" style={{ border: '8px solid white' }} />
                  <p className="text-center fst-italic small mb-0">{post.caption}</p>
                </div>
              )}
              {post.type === 'audio' && (
                <div className="mb-3 bg-light p-2 d-flex align-items-center justify-content-center rounded">
                  <span>Audio: {post.caption}</span>
                </div>
              )}
              <p className="card-text">{post.content}</p>
              <div className="mb-3">
                {post.tags.map((tag, index) => (
                  <span key={index} className="badge bg-secondary me-1 mb-1">#{tag}</span>
                ))}
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <button 
                  onClick={() => handleLike(post.id)}
                  className="btn btn-sm btn-outline-primary"
                >
                  <ThumbsUp size={20} className="me-1" />
                  <span>{post.likes}</span>
                </button>
                <button onClick={() => handleShare(post.id)} className="btn btn-sm btn-outline-secondary">
                  <Share2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {loading && <p className="text-center">Loading more posts...</p>}
      </main>

      {/* Create Post Modal (same as before) */}
      {isCreatePostOpen && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Post</h5>
                <button type="button" className="btn-close" onClick={() => setIsCreatePostOpen(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreatePost}>
                  <div className="mb-3">
                    <label className="form-label">
                      File
                      <input
                        type="file"
                        onChange={(e) => setNewPost({...newPost, file: e.target.files[0]})}
                        className="form-control"
                      />
                    </label>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Caption
                      <input
                        type="text"
                        value={newPost.caption}
                        onChange={(e) => setNewPost({...newPost, caption: e.target.value})}
                        className="form-control"
                      />
                    </label>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Tags (comma-separated)
                      <input
                        type="text"
                        value={newPost.tags}
                        onChange={(e) => setNewPost({...newPost, tags: e.target.value})}
                        className="form-control"
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                  >
                    Create Post
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;